import React, { useState } from 'react';
import './WeekCalendar.css';
import {
  addDays, weekdays, formatWeekRange, ymd, dayStatus,
  groupByDay, formatTime, dayName, dayShort, repLabel,
} from '../utils/appointmentWeek';

// A Monday–Friday week calendar. Past days are muted, today is highlighted,
// future days are upcoming. Optional weekend strip appears only if appointments
// fall on Sat/Sun so nothing is ever hidden. Cards are compact; clicking one
// opens a detail popup with the full notes. When `editable`, the popup also
// offers Edit/Delete.
const WeekCalendar = ({
  weekStart,
  appointments = [],
  onPrev,
  onNext,
  onToday,
  editable = false,
  onEdit,
  onDelete,
  showNav = true,
}) => {
  const [detail, setDetail] = useState(null);

  const byDay = groupByDay(appointments);
  const days = weekdays(weekStart);

  const sat = addDays(weekStart, 5);
  const sun = addDays(weekStart, 6);
  const weekendDays = [sat, sun].filter((d) => (byDay[ymd(d)] || []).length > 0);

  const fullDateTime = (dtStr) => {
    const d = new Date(dtStr);
    if (isNaN(d)) return dtStr;
    return d.toLocaleString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const renderCard = (appt) => (
    <button key={appt.id} className="wc-card" onClick={() => setDetail(appt)}>
      <div className="wc-card-time">{formatTime(appt.appointment_datetime)}</div>
      <div className="wc-card-customer">{appt.customer_name}</div>
      <div className="wc-card-rep">{repLabel(appt)}</div>
      {appt.notes && <div className="wc-card-hasnote">Note ›</div>}
    </button>
  );

  const renderDay = (date) => {
    const key = ymd(date);
    const status = dayStatus(key);
    const list = byDay[key] || [];
    return (
      <div key={key} className={`wc-day wc-day-${status}`}>
        <div className="wc-day-header">
          <span className="wc-day-name">{dayName(date)}</span>
          <span className="wc-day-date">{dayShort(date)}</span>
          {status === 'today' && <span className="wc-today-badge">TODAY</span>}
        </div>
        <div className="wc-day-body">
          {list.length === 0
            ? <div className="wc-day-empty">—</div>
            : list.map(renderCard)}
        </div>
      </div>
    );
  };

  return (
    <div className="wc-wrap">
      {showNav && (
        <div className="wc-nav">
          <button className="wc-nav-btn" onClick={onPrev}>◀ Previous Week</button>
          <div className="wc-nav-center">
            <div className="wc-week-range">Week of {formatWeekRange(weekStart)}</div>
            <button className="wc-today-link" onClick={onToday}>This Week</button>
          </div>
          <button className="wc-nav-btn" onClick={onNext}>Next Week ▶</button>
        </div>
      )}

      <div className="wc-grid">
        {days.map(renderDay)}
      </div>

      {weekendDays.length > 0 && (
        <div className="wc-weekend">
          <div className="wc-weekend-label">Weekend</div>
          <div className="wc-weekend-grid">
            {weekendDays.map(renderDay)}
          </div>
        </div>
      )}

      {detail && (
        <div className="wc-modal-overlay" onClick={() => setDetail(null)}>
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="wc-modal-close" onClick={() => setDetail(null)}>×</button>
            <div className="wc-modal-when">{fullDateTime(detail.appointment_datetime)}</div>
            <div className="wc-modal-row">
              <span className="wc-modal-label">Customer</span>
              <span className="wc-modal-value">{detail.customer_name}</span>
            </div>
            <div className="wc-modal-row">
              <span className="wc-modal-label">Rep</span>
              <span className="wc-modal-value">{repLabel(detail)}</span>
            </div>
            <div className="wc-modal-row">
              <span className="wc-modal-label">Notes</span>
              <span className="wc-modal-value wc-modal-notes">
                {detail.notes ? detail.notes : <em>No notes</em>}
              </span>
            </div>
            {editable && (
              <div className="wc-modal-actions">
                <button
                  className="wc-edit-btn"
                  onClick={() => { const a = detail; setDetail(null); onEdit && onEdit(a); }}
                >
                  Edit
                </button>
                <button
                  className="wc-delete-btn"
                  onClick={() => { const id = detail.id; setDetail(null); onDelete && onDelete(id); }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekCalendar;
